import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { AppInsightService, LobbyType, LogEvents } from '@rct/lynx-lobbies-shared-lib';
import { Subscription } from 'rxjs';
import { LYNX_STORAGE_KEYS } from '../../../constants/lynx-storage-keys.constant';
import { LynxAuthService } from '../../../services/auth.service';
import { LynxLobbyService } from '../../../services/lobby.service';
import { LynxLocalizeRouterService } from '../../../services/localize-router.service';
import { LynxModalService } from '../../../services/modal.service';

@Component({
    selector: 'lynx-auth-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class AuthLoginComponent implements OnInit, OnDestroy {
  public loginError: string;
  public isAllowRememberMe = false;
  loginForm: UntypedFormGroup;
  subscription = new Subscription();

  private router = inject(Router);
  private formBuilder = inject(UntypedFormBuilder);
  private authService = inject(LynxAuthService);
  private localizeRouterService = inject(LynxLocalizeRouterService);
  private modalService = inject(LynxModalService);
  private appInsightService = inject(AppInsightService);
  private lobbyService = inject(LynxLobbyService);

  constructor() {
    this.loginForm = this.formBuilder.group({
      account: ['', Validators.required],
      password: ['', Validators.required],
      rememberMe: [false]
    });
  }

  get loginErrorMsg() {
    return this.loginError
      ? this.loginError
      : this.isAccountError
        ? 'Error.AuthLogin.Account'
        : this.isPasswordError
          ? 'Error.AuthLogin.Password'
          : '';
  }

  get isAccountError() {
    return (
      (this.loginForm.get('account').dirty ||
        this.loginForm.get('account').touched) &&
      this.loginForm.get('account').hasError('required')
    );
  }

  get isPasswordError() {
    return (
      (this.loginForm.get('password').dirty ||
        this.loginForm.get('password').touched) &&
      this.loginForm.get('password').hasError('required')
    );
  }

  ngOnInit(): void {
    if (this.authService.isLoggedIn) {
      this.localizeRouterService.translateRoute('home');
    }
    this.loginForm.get('rememberMe').valueChanges.subscribe((status) => {
      if (!status) {
        this.loginForm.patchValue({ password: '' });
        this.authService.removeAccount();
      }
    });
    const theme = this.updateRememberMe();
    this.subscription.add(theme);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onSubmit() {
    this.loginForm.markAllAsTouched();
    const account = this.loginForm.get('account').value;
    const password = this.loginForm.get('password').value;
    const isRememberMe = this.loginForm.get('rememberMe').value;

    if (this.loginForm.invalid) {
      return;
    }
    this.loginError = null;
    this.modalService.showLoading();
    this.authService.login(account, password, isRememberMe).subscribe(
      (_) => {
        this.localizeRouterService.translateRoute('home');
        this.modalService.hideLoading();
        this.appInsightService.logEvent(
          { storageKeys: LYNX_STORAGE_KEYS, lobbyType: LobbyType.Lynx },
          LogEvents.SuccessLogin
        );
        this.appInsightService.gaEvent(
          { storageKeys: LYNX_STORAGE_KEYS, lobbyType: LobbyType.Lynx },
          LogEvents.SuccessLogin
        );
      },
      (error) => {
        this.modalService.hideLoading();
        this.appInsightService.logEvent(
          { storageKeys: LYNX_STORAGE_KEYS, lobbyType: LobbyType.Lynx },
          LogEvents.ErrorLogin
        );
        this.appInsightService.gaEvent(
          { storageKeys: LYNX_STORAGE_KEYS, lobbyType: LobbyType.Lynx },
          LogEvents.ErrorLogin
        );
        const strategies = [
          {
            condition: () =>
              error.status === 401 || error.error.Code === '70014',
            action: () => 'Error.AuthLogin.LoginFailed'
          },
          {
            condition: () => error.status === 500,
            action: () => 'Error.AuthLogin.AccountLocked'
          },
          {
            condition: () => true,
            action: () => 'Error.AuthLogin.InternalServiceError'
          }
        ];
        for (const strategy of strategies) {
          if (strategy.condition()) {
            this.loginError = strategy.action();
            break;
          }
        }
      }
    );
  }

  updateRememberMe() {
    return this.lobbyService.theme$.subscribe((theme) => {
      this.isAllowRememberMe = theme.allowRememberMe;
      const data = this.authService.getAccount();
      if (data) {
        this.loginForm.patchValue({ account: data.account });
        this.loginForm.patchValue({ password: data.password });
        this.loginForm.patchValue({ rememberMe: true });
      }
    });
  }
}
